import React from "react";
import {
  FiCamera,
  FiCreditCard,
  FiFacebook,
  FiGlobe,
  FiImage,
  FiInstagram,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiSave,
  FiShoppingBag,
  FiTag,
  FiTruck,
} from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AdminField from "../../_shared/ui/AdminField";
import AdminRichTextEditor from "../../_shared/ui/AdminRichTextEditor";

import {
  COMMERCE_CATEGORY_SUGGESTIONS,
  COMMERCES_FORM_HELPERS,
} from "../data/commercesAdminData";

export default function CommercesFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  onClose,
  onChange,
  onSave,
  onOpenMediaPicker,
}) {
  const logoUrl = form.logo_url ? toAbsolutePublicUrl(form.logo_url) : "";
  const photoUrl = form.photo_url ? toAbsolutePublicUrl(form.photo_url) : "";

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier le commerce" : "Nouveau commerce"}
      subtitle={
        isEdit
          ? "Mettez à jour la fiche puis enregistrez les modifications."
          : "Ajoutez un commerce ou un artisan à l’annuaire local."
      }
      icon={FiShoppingBag}
      size="full"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Identité du commerce
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField
                label="Nom du commerce"
                required
                error={fieldErrors.nom_commerce}
              >
                <input
                  value={form.nom_commerce}
                  onChange={(event) =>
                    onChange("nom_commerce", event.target.value)
                  }
                  placeholder="Nom du commerce"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Catégorie">
                <input
                  value={form.categorie}
                  onChange={(event) =>
                    onChange("categorie", event.target.value)
                  }
                  placeholder="Boulangerie, Restaurant..."
                  list="commerce-form-categories"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />

                <datalist id="commerce-form-categories">
                  {COMMERCE_CATEGORY_SUGGESTIONS.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              </AdminField>

              <AdminField label="SIRET">
                <input
                  value={form.siret}
                  onChange={(event) => onChange("siret", event.target.value)}
                  placeholder="12345678901234"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Tags">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiTag className="text-slate-400" />

                  <input
                    value={form.tags}
                    onChange={(event) => onChange("tags", event.target.value)}
                    placeholder="bio,local,livraison..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Images et liens
            </h3>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <AdminField label="Logo">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    value={form.logo_url}
                    onChange={(event) =>
                      onChange("logo_url", event.target.value)
                    }
                    placeholder="https://... ou /uploads/..."
                    className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />

                  <AdminButton
                    icon={FiImage}
                    variant="soft"
                    onClick={() => onOpenMediaPicker("logo_url")}
                    className="sm:min-w-[150px]"
                  >
                    Logo
                  </AdminButton>
                </div>
              </AdminField>

              <AdminField label="Photo principale">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    value={form.photo_url}
                    onChange={(event) =>
                      onChange("photo_url", event.target.value)
                    }
                    placeholder="https://... ou /uploads/..."
                    className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />

                  <AdminButton
                    icon={FiCamera}
                    variant="soft"
                    onClick={() => onOpenMediaPicker("photo_url")}
                    className="sm:min-w-[150px]"
                  >
                    Photo
                  </AdminButton>
                </div>
              </AdminField>

              <AdminField label="Site web">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiGlobe className="text-slate-400" />

                  <input
                    value={form.website}
                    onChange={(event) =>
                      onChange("website", event.target.value)
                    }
                    placeholder="https://..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Facebook">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiFacebook className="text-slate-400" />

                  <input
                    value={form.facebook}
                    onChange={(event) =>
                      onChange("facebook", event.target.value)
                    }
                    placeholder="https://facebook.com/..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Instagram">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiInstagram className="text-slate-400" />

                  <input
                    value={form.instagram}
                    onChange={(event) =>
                      onChange("instagram", event.target.value)
                    }
                    placeholder="https://instagram.com/..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Contact
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <AdminField label="Nom du contact">
                <input
                  value={form.contact_nom}
                  onChange={(event) =>
                    onChange("contact_nom", event.target.value)
                  }
                  placeholder="Nom du référent"
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Fonction">
                <input
                  value={form.contact_fonction}
                  onChange={(event) =>
                    onChange("contact_fonction", event.target.value)
                  }
                  placeholder="Gérant, responsable..."
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Téléphone">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiPhone className="text-slate-400" />

                  <input
                    value={form.tel}
                    onChange={(event) => onChange("tel", event.target.value)}
                    placeholder="05..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Email" error={fieldErrors.mail}>
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMail className="text-slate-400" />

                  <input
                    type="email"
                    value={form.mail}
                    onChange={(event) => onChange("mail", event.target.value)}
                    placeholder="contact@commerce.fr"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Adresse et localisation
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <AdminField label="Adresse">
                <textarea
                  value={form.adresse}
                  onChange={(event) => onChange("adresse", event.target.value)}
                  rows={3}
                  placeholder="Adresse complète"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <div className="grid gap-4 sm:grid-cols-2">
                <AdminField label="Code postal">
                  <input
                    value={form.code_postal}
                    onChange={(event) =>
                      onChange("code_postal", event.target.value)
                    }
                    placeholder="33000"
                    className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />
                </AdminField>

                <AdminField label="Ville">
                  <input
                    value={form.ville}
                    onChange={(event) => onChange("ville", event.target.value)}
                    placeholder="Ville"
                    className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                  />
                </AdminField>
              </div>

              <AdminField
                label="Coordonnées GPS"
                error={fieldErrors.gps_coordinates}
                help="Format attendu : latitude, longitude. Exemple : 44.87081481416553, -0.603661378488808"
              >
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiMapPin className="text-slate-400" />

                  <input
                    value={form.gps_coordinates}
                    onChange={(event) =>
                      onChange("gps_coordinates", event.target.value)
                    }
                    placeholder="44.87081481416553, -0.603661378488808"
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Services et informations pratiques
            </h3>

            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <AdminField label="Moyens de paiement">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiCreditCard className="text-slate-400" />

                  <input
                    value={form.moyens_paiement}
                    onChange={(event) =>
                      onChange("moyens_paiement", event.target.value)
                    }
                    placeholder="CB, espèces..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>

              <AdminField label="Accessibilité">
                <input
                  value={form.accessibilite}
                  onChange={(event) =>
                    onChange("accessibilite", event.target.value)
                  }
                  placeholder="PMR, parking..."
                  className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
                />
              </AdminField>

              <AdminField label="Services">
                <div className="flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 transition focus-within:border-sky-400 focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                  <FiTruck className="text-slate-400" />

                  <input
                    value={form.services}
                    onChange={(event) =>
                      onChange("services", event.target.value)
                    }
                    placeholder="Livraison, Click & Collect..."
                    className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none dark:text-slate-100"
                  />
                </div>
              </AdminField>
            </div>
          </section>

          <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
            <h3 className="text-base font-bold text-slate-950 dark:text-white">
              Description
            </h3>

            <div className="mt-4">
              <AdminRichTextEditor
                value={form.description_commerce}
                onChange={(html) => onChange("description_commerce", html)}
                placeholder="Présentez le commerce, ses activités, ses horaires ou ses informations utiles..."
              />
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <div className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Aperçu photo
              </p>
            </div>

            <div className="p-4">
              {photoUrl ? (
                <>
                  <img
                    src={photoUrl}
                    alt="Photo"
                    className="aspect-[16/10] w-full rounded-[1.25rem] object-cover"
                  />

                  <p className="mt-3 break-all text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {photoUrl}
                  </p>
                </>
              ) : (
                <div className="flex aspect-[16/10] flex-col items-center justify-center rounded-[1.25rem] bg-white text-center text-slate-400 dark:bg-slate-900 dark:text-slate-500">
                  <FiCamera className="text-4xl" />

                  <p className="mt-3 text-sm font-bold">Aucune photo</p>

                  <p className="mt-1 max-w-[220px] text-xs leading-5">
                    Ajoutez une photo pour rendre la fiche plus attractive.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Aperçu logo
              </p>
            </div>

            <div className="p-4">
              {logoUrl ? (
                <>
                  <div className="flex aspect-square items-center justify-center rounded-[1.25rem] bg-white p-5 dark:bg-slate-900">
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>

                  <p className="mt-3 break-all text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {logoUrl}
                  </p>
                </>
              ) : (
                <div className="flex aspect-square flex-col items-center justify-center rounded-[1.25rem] bg-white text-center text-slate-400 dark:bg-slate-900 dark:text-slate-500">
                  <FiImage className="text-4xl" />

                  <p className="mt-3 text-sm font-bold">Aucun logo</p>

                  <p className="mt-1 max-w-[220px] text-xs leading-5">
                    Ajoutez un logo pour identifier le commerce.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <button
              type="button"
              onClick={() => onChange("is_active", !form.is_active)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_active ? "Commerce publié" : "Commerce en brouillon"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  {form.is_active
                    ? "Il sera visible sur le site public."
                    : "Il restera masqué du public."}
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_active ? "bg-emerald-500" : "bg-amber-500"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_active ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="rounded-[1.7rem] border border-orange-100 bg-orange-50 p-4 dark:border-orange-500/20 dark:bg-orange-500/10">
            <p className="text-sm font-bold text-orange-900 dark:text-orange-200">
              Conseils pratiques
            </p>

            <ul className="mt-3 space-y-2">
              {COMMERCES_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-orange-800 dark:text-orange-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}
